"use client";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { useId, useState } from "react";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";

// Initialize the countries with English locale
countries.registerLocale(enLocale);

// Define most common countries
const MOST_COMMON_COUNTRIES = [
  { value: "US", label: "United States of America" },
  { value: "GB", label: "United Kingdom" },
];

// Get all countries and convert to array format
const countryList = Object.entries(countries.getNames("en")).map(([code, name]) => ({
  value: code,
  label: name,
}));

// Sort countries alphabetically
countryList.sort((a, b) => a.label.localeCompare(b.label));

interface CountrySearchProps {
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
}

export default function CountrySearch({ value = "", onChange, label }: CountrySearchProps) {
  const id = useId();
  const [open, setOpen] = useState<boolean>(false);

  return (
    <div className="*:not-first:mt-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="bg-background hover:bg-background border-input w-full justify-between px-3 font-normal outline-offset-0 outline-none focus-visible:outline-[3px]"
          >
            <span className={cn("truncate", !value && "text-muted-foreground")}>
              {value
                ? countryList.find((country) => country.value === value)?.label
                : "Select country"}
            </span>
            <ChevronDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="border-input w-full min-w-[var(--radix-popper-anchor-width)] p-0"
          align="start"
        >
          <Command shouldFilter={true}>
            <CommandInput placeholder="Search country..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup heading="Most Common">
                {MOST_COMMON_COUNTRIES.map((country) => (
                  <CommandItem
                    key={country.value}
                    value={country.label}
                    onSelect={(currentValue: string) => {
                      const selectedCountry = countryList.find(c => c.label === currentValue);
                      if (selectedCountry) {
                        onChange?.(selectedCountry.value === value ? "" : selectedCountry.value);
                        setOpen(false);
                      }
                    }}
                  >
                    {country.label}
                    {value === country.value && <CheckIcon size={16} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandGroup heading="All Countries">
                {countryList.map((country) => (
                  <CommandItem
                    key={country.value}
                    value={country.label}
                    onSelect={(currentValue: string) => {
                      const selectedCountry = countryList.find(c => c.label === currentValue);
                      if (selectedCountry) {
                        onChange?.(selectedCountry.value === value ? "" : selectedCountry.value);
                        setOpen(false);
                      }
                    }}
                  >
                    {country.label}
                    {value === country.value && <CheckIcon size={16} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
